'use client'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import type { NavItem } from './AppSidebar'

export function NavGroup({ items, title, children }: { title?: ReactNode; items: NavItem[]; children?: ReactNode }) {
  const { isMobile, setOpenMobile, open: sidebarOpen, openMobile: sidebarOpenMobile } = useSidebar()
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({})
  const [lastItemsHash, setLastItemsHash] = useState<string>('')
  const [popoverOpenStates, setPopoverOpenStates] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const currentHash = JSON.stringify(items.map((item) => item.title))

    // If items have changed (different navigation context), open all groups with subitems
    if (currentHash !== lastItemsHash) {
      const newOpenStates: Record<string, boolean> = {}
      items.forEach(({ title, subItems }) => {
        if (subItems && subItems.length > 0) {
          newOpenStates[title] = true
        }
      })
      setOpenStates(newOpenStates)
      setLastItemsHash(currentHash)
    }
  }, [items, lastItemsHash])

  const handleToggle = (title: string, isOpen: boolean) => {
    setOpenStates((prev) => ({
      ...prev,
      [title]: isOpen,
    }))
  }

  const handleMobileSidebarClose = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const handlePopoverLinkClick = (title: string) => {
    setPopoverOpenStates((prev) => ({
      ...prev,
      [title]: false,
    }))
    handleMobileSidebarClose()
  }

  return (
    <SidebarGroup>
      {title && <SidebarGroupLabel>{title}</SidebarGroupLabel>}
      <SidebarMenu>
        {children}
        {items
          .filter((item) => !item.hidden)
          .map(({ title, icon: Icon, isActive, linkOptions, content, subItems }) => {
            const hasSubItems = (subItems?.length ?? 0) > 0
            const isOpen = openStates[title] || false

            if (!hasSubItems) {
              return (
                <SidebarMenuItem key={title}>
                  <SidebarMenuButton asChild tooltip={title} isActive={isActive}>
                    <Link {...linkOptions} onClick={() => handleMobileSidebarClose()}>
                      {Icon && <Icon />}
                      <span>{title}</span>
                      {content}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            }

            if (!sidebarOpenMobile && !sidebarOpen) {
              return (
                <SidebarMenuItem key={title}>
                  <Popover
                    open={popoverOpenStates[title] ?? false}
                    onOpenChange={(isOpen) =>
                      setPopoverOpenStates((prev) => ({
                        ...prev,
                        [title]: isOpen,
                      }))
                    }
                  >
                    <PopoverTrigger asChild>
                      <SidebarMenuButton tooltip={title} isActive={isActive}>
                        {Icon && <Icon />}
                      </SidebarMenuButton>
                    </PopoverTrigger>
                    <PopoverContent side="right" align="start" className="w-56 p-2">
                      <div className="flex flex-col gap-1">
                        {subItems
                          ?.filter((subItem) => !subItem.hidden)
                          .map((subItem) => (
                            <Link
                              key={subItem.title}
                              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                              {...subItem.linkOptions}
                              onClick={() => handlePopoverLinkClick(title)}
                            >
                              {subItem.icon && <subItem.icon className="h-4 w-4" />}
                              <span>{subItem.title}</span>
                              {subItem.content}
                            </Link>
                          ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </SidebarMenuItem>
              )
            }

            return (
              <Collapsible
                key={title}
                className="group"
                open={isOpen}
                onOpenChange={(isOpen) => handleToggle(title, isOpen)}
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={title} isActive={isActive && !isOpen}>
                      {Icon && <Icon />}
                      <span>{title}</span>
                      {content}
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {subItems
                        ?.filter((subItem) => !subItem.hidden)
                        .map((subItem) => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton
                              asChild
                              className="data-[active=true]:font-medium"
                              isActive={subItem.isActive}
                            >
                              <Link
                                className="flex w-full items-center gap-2"
                                {...subItem.linkOptions}
                                onClick={() => handleMobileSidebarClose()}
                              >
                                {subItem.icon && <subItem.icon className="h-4 w-4" />}
                                <span>{subItem.title}</span>
                                {subItem.content}
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
          })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
